import { useState } from 'react';
import { ArrowDown, ArrowUp, Check, Plus, Trash2, X } from 'lucide-react';
import type { IndexModel, TableModel } from '@bits2schema/shared';
import { Modal } from '../../components/Modal';
import { useT } from '../../i18n';
import { defaultIndexName, ensureUniqueName } from '../mutations';

interface IndexDialogProps {
  table: TableModel;
  initial: IndexModel | null;
  onCancel: () => void;
  onConfirm: (columns: string[], isUnique: boolean, name: string) => void;
}

function moveItem<T>(list: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Formulář pro vytvoření/editaci indexu (E4-US10) — pořadí sloupců je významné. */
export function IndexDialog({ table, initial, onCancel, onConfirm }: IndexDialogProps) {
  const t = useT();
  const [name, setName] = useState(initial?.name ?? '');
  const [selected, setSelected] = useState<string[]>(initial?.columns ?? []);
  const [isUnique, setIsUnique] = useState(initial?.isUnique ?? false);
  const [pickerValue, setPickerValue] = useState('');

  const available = table.columns.map((column) => column.name).filter((columnName) => !selected.includes(columnName));
  const valid = selected.length > 0;

  const confirm = () => {
    if (!valid) return;
    const existingNames = table.indexes.filter((candidate) => candidate.id !== initial?.id).map((candidate) => candidate.name);
    const finalName = name.trim() ? ensureUniqueName(name.trim(), existingNames) : defaultIndexName(table, selected);
    onConfirm(selected, isUnique, finalName);
  };

  return (
    <Modal
      title={initial ? t('schema.dialog.index.editTitle') : t('schema.dialog.index.createTitle')}
      onClose={onCancel}
      actions={
        <>
          <button type="button" onClick={onCancel}>
            <X size={16} />
            {t('common.cancel')}
          </button>
          <button type="button" className="primary" disabled={!valid} onClick={confirm}>
            <Check size={16} />
            {initial ? t('schema.dialog.index.editTitle') : t('common.create')}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label htmlFor="index-name">{t('schema.dialog.index.nameLabel')}</label>
          <input id="index-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>

        <div className="field">
          <label>
            <input type="checkbox" checked={isUnique} onChange={(event) => setIsUnique(event.target.checked)} />
            {t('schema.dialog.index.uniqueLabel')}
          </label>
        </div>

        <div className="field">
          <span>{t('schema.dialog.index.columnsLabel')}</span>
          <ul className="inspector__column-list">
            {selected.map((columnName, index) => (
              <li key={columnName}>
                <span className="inspector__column-name">{columnName}</span>
                <div className="toolbar__actions">
                  <button
                    type="button"
                    className="icon-btn"
                    disabled={index === 0}
                    title={t('schema.dialog.index.moveUp')}
                    onClick={() => setSelected(moveItem(selected, index, -1))}
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    disabled={index === selected.length - 1}
                    title={t('schema.dialog.index.moveDown')}
                    onClick={() => setSelected(moveItem(selected, index, 1))}
                  >
                    <ArrowDown size={16} />
                  </button>
                  <button type="button" onClick={() => setSelected(selected.filter((name) => name !== columnName))}>
                    <Trash2 size={16} />
                    {t('schema.dialog.index.remove')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {!valid && <p className="hint">{t('schema.dialog.index.columnsRequired')}</p>}
          {available.length > 0 && (
            <div className="inline-fields">
              <select value={pickerValue} onChange={(event) => setPickerValue(event.target.value)}>
                <option value="" disabled>
                  {t('schema.dialog.index.columnsLabel')}
                </option>
                {available.map((columnName) => (
                  <option key={columnName} value={columnName}>
                    {columnName}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!pickerValue}
                onClick={() => {
                  if (!pickerValue) return;
                  setSelected([...selected, pickerValue]);
                  setPickerValue('');
                }}
              >
                <Plus size={16} />
                {t('schema.dialog.index.addColumn')}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
